import { Link, useLocation, useNavigate } from "react-router-dom";
import { Home, Heart, Menu, Search, ClipboardList } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

/**
 * Premium bottom navigation bar (mobile only).
 * 4 tabs + a floating center "+" action button.
 * All items link to EXISTING routes only — no new features.
 */
export default function BottomNav() {
  const location = useLocation();
  const nav = useNavigate();
  const { user } = useAuth();

  const pathname = location.pathname;

  // Hide on internal dashboards (they have their own layout).
  // Keep visible on /dashboard (user's Orders page) so bottom nav stays accessible.
  const HIDE_ON = [
    "/shop-dashboard",
    "/admin",
    "/delivery-dashboard",
  ];
  if (HIDE_ON.some(p => pathname.startsWith(p))) return null;
  // Hide on the language chooser
  if (pathname === "/lang") return null;

  const isActive = (href, exact = false) => {
    if (exact) return pathname === href;
    return pathname === href || pathname.startsWith(href + "/");
  };

  // Center "+" action:
  // - Guest → login/register
  // Center Search button — jumps to the app-wide search page
  const searchTarget = "/search";

  // Right-most menu item — opens the drawer (no navigation)
  return (
    <>
      {/* Bottom nav wrapper with safe-area padding for iOS home indicator */}
      <nav
        className="md:hidden fixed bottom-0 inset-x-0 z-40"
        data-testid="mobile-bottom-nav"
        aria-label="التنقل السفلي"
      >
        <div className="relative bg-slate-900/85 backdrop-blur-xl border-t border-white/10 pt-1 pb-safe px-2">
          <div className="max-w-md mx-auto grid grid-cols-5 items-end">
            {/* Home */}
            <NavTab
              to="/"
              icon={Home}
              label="الرئيسية"
              active={isActive("/", true)}
              testId="bnav-home"
            />
            {/* Favorites (heart) */}
            <NavTab
              to="/favorites"
              icon={Heart}
              label="المفضلة"
              active={isActive("/favorites")}
              testId="bnav-favorites"
            />
            {/* Center Search floating button — big & prominent */}
            <div className="flex justify-center">
              <button
                type="button"
                onClick={() => nav(searchTarget)}
                data-testid="bnav-search"
                aria-label="ابحث عن محل"
                title="ابحث عن محل"
                className="relative -mt-6 size-12 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-2xl glow-primary hover:scale-105 active:scale-95 transition-transform"
              >
                <Search className="size-5 stroke-[2.5]" />
                {/* Glow ring */}
                <span className="absolute inset-0 rounded-full ring-2 ring-primary/25 animate-pulse pointer-events-none" />
              </button>
            </div>
            {/* Orders */}
            <NavTab
              to="/dashboard"
              icon={ClipboardList}
              label="الطلبات"
              active={isActive("/dashboard")}
              testId="bnav-orders"
            />
            {/* Menu — opens the mobile drawer (settings, language, theme, logout) */}
            <button
              type="button"
              onClick={() => {
                // Dispatch a global event that the Header drawer listens for
                window.dispatchEvent(new CustomEvent("kh-open-drawer"));
              }}
              data-testid="bnav-menu"
              className="flex flex-col items-center justify-end gap-0.5 py-1 min-h-[44px] group"
            >
              <Menu className="size-5 transition-colors text-slate-400 group-hover:text-slate-200" />
              <span className="text-[10px] font-medium transition-colors text-slate-400 group-hover:text-slate-200">
                القائمة
              </span>
            </button>
          </div>
        </div>
      </nav>

      {/* Spacer to prevent last page content from being hidden behind the nav on mobile */}
      <div className="md:hidden h-14" aria-hidden="true" />
    </>
  );
}

function NavTab({ to, icon: Icon, label, active, testId }) {
  return (
    <Link
      to={to}
      data-testid={testId}
      className="flex flex-col items-center justify-end gap-0.5 py-1 min-h-[44px] group"
    >
      <Icon
        className={`size-5 transition-colors ${
          active
            ? "text-primary stroke-[2.4]"
            : "text-slate-400 group-hover:text-slate-200"
        }`}
      />
      <span
        className={`text-[10px] font-medium transition-colors ${
          active ? "text-primary" : "text-slate-400 group-hover:text-slate-200"
        }`}
      >
        {label}
      </span>
    </Link>
  );
}
